import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useBlockSlot,
  SectionContextProvider,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { PropertyInfoPanel } from './PropertyInfoPanel';

// ============================================================================
// Schema definitions (module-level literals — extractor follows one level)
// ============================================================================

// Address slot group — mirrors the Footer's address pattern so the visit-us
// card becomes a link to the property's Google Place page when set. The
// schema literal references this constant (extractor follows one level).
const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: {
      label: 'Address Text',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, City, ST 00000',
    },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at
    // render time inside PropertyInfoPanel. Editor overrides still win.
    url: {
      label: 'URL',
      type: 'url',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

// ============================================================================
// Component
// ============================================================================

export function ContactForm() {
  const prefix = 'contactform';
  const formBlockPath = `${prefix}.form_block`;

  const formSlot = useBlockSlot(formBlockPath, {
    defaultValue: 'multihub-lead-form',
  });

  return (
    <>
      <div className="cf-section">
        <div className="cf-container">
          <div className="cf-layout">
            {/* Left: lead form */}
            <div className="cf-form-wrap">
              <EditableToken path={formBlockPath} as="div">
                {formSlot ? (
                  <SectionContextProvider sectionId={formSlot.sectionId}>
                    <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                  </SectionContextProvider>
                ) : (
                  <div className="cf-form-empty-card">
                    <p className="cf-form-empty">No form block selected</p>
                  </div>
                )}
              </EditableToken>
            </div>

            {/* Right: contact info cards stacked vertically */}
            <aside className="cf-info-rail">
              <PropertyInfoPanel prefix={prefix} addressDefinition={addressLinkDef} />
            </aside>
          </div>
        </div>
      </div>

      {/* Schema-lint anchors — static paths so the linter can match each schema key.
          The live EditableTokens in PropertyInfoPanel use dynamic paths from prefix. */}
      <EditableToken path="contactform.form_block"   as="div"  className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="contactform.visit_label"  as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableSlotGroup
        groupPath="contactform.address"
        definition={addressLinkDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableToken path="contactform.call_label"   as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="contactform.phone"        as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="contactform.hours_label"  as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>

      <style>{`
        .cf-section {
          background: var(--vibe-color-background);
          padding: 2.5rem 1.5rem;
        }
        @media (min-width: 768px) {
          .cf-section { padding: 4rem 2rem; }
        }
        .cf-container {
          max-width: 72rem;
          margin: 0 auto;
        }

        /* Two-column layout: form on the left, info cards on the right */
        .cf-layout {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
          align-items: start;
        }
        @media (min-width: 1024px) {
          .cf-layout {
            grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);
            gap: 2.5rem;
          }
        }

        .cf-form-wrap {
          width: 100%;
          border-radius: var(--vibe-radius-lg, 12px);
          overflow: hidden;
          box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
          background: white;
        }
        /* Multihub lead-form submit uses inline colors, so hover must beat them
           (!important). Swap primary → secondary for a clear color hover. */
        .cf-form-wrap .mh-lead-form-submit-button,
        .cf-form-wrap .mh-lead-form-success-cta {
          transition: background-color 0.3s ease, color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;
        }
        .cf-form-wrap .mh-lead-form-submit-button:hover:not(:disabled),
        .cf-form-wrap .mh-lead-form-success-cta:hover {
          background-color: var(--vibe-color-secondary) !important;
          color: var(--vibe-color-text-on-secondary, #fff) !important;
          transform: translateY(-2px);
          box-shadow: 0 6px 18px color-mix(in srgb, var(--vibe-color-secondary) 40%, transparent);
        }
        .cf-form-wrap .mh-lead-form-submit-button:active:not(:disabled),
        .cf-form-wrap .mh-lead-form-success-cta:active {
          transform: translateY(0);
          box-shadow: none;
        }
        @media (prefers-reduced-motion: reduce) {
          .cf-form-wrap .mh-lead-form-submit-button:hover:not(:disabled),
          .cf-form-wrap .mh-lead-form-submit-button:active:not(:disabled),
          .cf-form-wrap .mh-lead-form-success-cta:hover,
          .cf-form-wrap .mh-lead-form-success-cta:active {
            transform: none;
            box-shadow: none;
          }
        }
        .cf-form-empty-card {
          background: white;
          border-radius: var(--vibe-radius-lg, 12px);
          padding: 2rem;
        }
        .cf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          text-align: center;
          margin: 0;
          padding: 2rem 0;
        }

        /* ── Right rail: stack the shared info cards vertically ── */
        .cf-info-rail { width: 100%; }
        .cf-info-rail .cf-info-cards {
          flex-direction: column;
          flex-wrap: nowrap;
          gap: 1rem;
        }
        .cf-info-rail .cf-card {
          flex: 0 0 auto;
          width: 100%;
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactForm',
  displayName: 'Contact Form',
  tokens: {
    'contactform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
    'contactform.visit_label': {
      label: 'Visit Us Label',
      type: 'text',
      defaultValue: 'Visit Us',
    },
    'contactform.call_label': {
      label: 'Call Us Label',
      type: 'text',
      defaultValue: 'Call Us',
    },
    'contactform.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '',
    },
    'contactform.hours_label': {
      label: 'Office Hours Label',
      type: 'text',
      defaultValue: 'Office Hours',
    },
  },
  slotGroups: {
    'contactform.address': addressLinkDef,
  },
  collectionBindings: {
    'contactform.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ContactForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactForm;
